/**
 * useExportPresentationPreview — renders the real deck for the Export modal
 * and returns a PNG per page plus that same file's download URL.
 *
 * The preview is the download: action-engine renders the PPTX/PDF once, the
 * pages are rasterised from those bytes, and Export downloads `downloadUrl`
 * instead of rendering again — so what the user sees is exactly what they get.
 *
 * Keyed by modal session + format + theme: switching back to a theme already
 * viewed in this session reuses its render; reopening the modal renders
 * fresh, so edits made to the story in between are picked up.
 */
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import { getApiErrorMessage } from "../services/apiErrors";
import { downloadPresentation } from "../services/storyPresentation/storyPresentationApi";
import { keepRenderIfUnchanged } from "../services/storyPresentation/storyPresentationMappers";
import type {
  PresentationDownloadFormatApi,
  PresentationDownloadResponseApi,
} from "../services/storyPresentation/storyPresentation.types";

// Below the signed URLs' 20-minute TTL, so a cached render is never offered
// with links that have already expired.
const PREVIEW_STALE_MS = 15 * 60 * 1000;
// How often an open deck asks whether the story changed. Unchanged content is
// a render-cache hit on the server (~0.3 s), so this is cheap.
const REFRESH_INTERVAL_MS = 60 * 1000;

export interface UseExportPresentationPreviewArgs {
  workspaceId: string | undefined;
  storyId: string | undefined;
  format: PresentationDownloadFormatApi;
  themeKey: string | undefined;
  /** Changes every time the modal (or the Presentation tab) opens. */
  sessionId: number | string;
  enabled: boolean;
  /**
   * Caps how many pages get rasterised/uploaded — omitted means "all" (the
   * Export modal's own use, which shows every page). The delivery
   * attachment-preview card passes 1, since it only ever shows page 1.
   */
  previewPageLimit?: number;
  /**
   * Keep the render current while it is on screen: re-check when the browser
   * tab regains focus and every minute while it is visible, so edits made
   * elsewhere (another tab, another user, a story refresh) show up without
   * leaving and reopening. The Presentation tab sets this; the Export modal
   * and delivery card are short-lived and render fresh on every open.
   */
  refreshWhileOpen?: boolean;
}

export interface UseExportPresentationPreviewResult {
  pageUrls: string[];
  /** The rendered file itself — Export downloads this. */
  downloadUrl: string | undefined;
  /** Its artifact id — a delivery attaches this exact file by id. */
  artifactId: string | undefined;
  /** Its size in bytes; null when unknown (or not rendered yet). */
  fileSizeBytes: number | null;
  /** No render to show yet (first load). */
  isRendering: boolean;
  /** A re-check is in flight while an earlier render is still shown. */
  isRefreshing: boolean;
  errorMessage: string | null;
  /** Re-check now (e.g. the story was just edited). */
  refresh: () => void;
}

export function useExportPresentationPreview({
  workspaceId,
  storyId,
  format,
  themeKey,
  sessionId,
  enabled,
  previewPageLimit,
  refreshWhileOpen = false,
}: UseExportPresentationPreviewArgs): UseExportPresentationPreviewResult {
  const query = useQuery({
    queryKey: queryKeys.stories.presentationPreview(
      workspaceId ?? "",
      storyId ?? "",
      format,
      themeKey ?? "",
      sessionId,
      previewPageLimit,
    ),
    queryFn: () =>
      downloadPresentation(workspaceId!, storyId!, format, {
        theme: themeKey,
        includePreview: true,
        previewPageLimit,
      }),
    enabled: enabled && Boolean(workspaceId && storyId && themeKey),
    staleTime: PREVIEW_STALE_MS,
    gcTime: PREVIEW_STALE_MS,
    // A render is expensive and deterministic for the session; a failure is
    // shown with the backend's reason rather than silently retried.
    retry: false,
    refetchOnWindowFocus: refreshWhileOpen ? "always" : false,
    refetchInterval: refreshWhileOpen ? REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    // Same deck back (content unchanged) → keep the URLs already on screen.
    structuralSharing: (previous, next) =>
      keepRenderIfUnchanged(
        previous as PresentationDownloadResponseApi | undefined,
        next as PresentationDownloadResponseApi,
        Date.now(),
      ),
  });

  return {
    pageUrls: query.data?.preview_urls ?? [],
    downloadUrl: query.data?.download_url,
    artifactId: query.data?.artifact_id,
    fileSizeBytes: query.data?.file_size_bytes ?? null,
    isRendering: query.isFetching && !query.data,
    isRefreshing: query.isFetching && Boolean(query.data),
    refresh: () => {
      void query.refetch();
    },
    errorMessage: query.isError
      ? getApiErrorMessage(query.error, "Couldn't render the preview.")
      : null,
  };
}
